import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Receipt, Plus, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function InvoicesPage() {
  const t = useT();
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", amount: "", description: "", due_date: "" });

  const load = async () => {
    const [i, s] = await Promise.all([apiClient.get("/invoices"), apiClient.get("/students")]);
    setInvoices(i.data.invoices); setStudents(s.data.students);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.student_id || !form.amount) return toast.error(t("Student and amount required"));
    await apiClient.post("/invoices", { ...form, amount: Number(form.amount) });
    setOpen(false); setForm({ student_id: "", amount: "", description: "", due_date: "" });
    load(); toast.success(t("Invoice created"));
  };
  const markPaid = async (id) => { await apiClient.post(`/invoices/${id}/mark-paid`); load(); };
  const del = async (id) => { if (!window.confirm(t("Delete invoice?"))) return; await apiClient.delete(`/invoices/${id}`); load(); };

  const outstanding = invoices.filter((i) => !i.paid).reduce((a, i) => a + (i.amount || 0), 0);
  const received = invoices.filter((i) => i.paid).reduce((a, i) => a + (i.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Receipt className="w-7 h-7" /> {t("Invoices")}</h1>
          <p style={{ color: "var(--app-text-muted)" }}>{t("Track payments received and outstanding.")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-pill btn-primary" data-testid="new-invoice-btn"><Plus className="w-4 h-4" /> {t("New invoice")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("New invoice")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t("Student *")}</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger data-testid="inv-student"><SelectValue placeholder={t("Pick a student")} /></SelectTrigger>
                  <SelectContent>{students.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("Amount (USD) *")}</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="inv-amount" /></div>
              <div><Label>{t("Description")}</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="inv-desc" /></div>
              <div><Label>{t("Due date")}</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <Button onClick={save} className="btn-pill btn-primary w-full justify-center h-10" data-testid="save-invoice">{t("Save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bento-card p-5"><div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Outstanding")}</div><div className="font-display text-3xl font-bold">${outstanding.toFixed(2)}</div></div>
        <div className="bento-card p-5"><div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Received")}</div><div className="font-display text-3xl font-bold">${received.toFixed(2)}</div></div>
        <div className="bento-card p-5"><div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Count")}</div><div className="font-display text-3xl font-bold">{invoices.length}</div></div>
      </div>

      <div className="bento-card overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--app-surface-2)" }}>
            <tr><th className="p-3 text-left">{t("Student *").replace(" *", "")}</th><th className="p-3 text-left">{t("Description")}</th><th className="p-3 text-right">{t("Amount")}</th><th className="p-3 text-left">{t("Due")}</th><th className="p-3">{t("Status")}</th><th></th></tr>
          </thead>
          <tbody>
            {invoices.map((i) => {
              const s = students.find((x) => x.id === i.student_id);
              return (
                <tr key={i.id} className="border-t" style={{ borderColor: "var(--app-border)" }} data-testid={`invoice-${i.id}`}>
                  <td className="p-3 font-semibold">{s?.name || "—"}</td>
                  <td className="p-3">{i.description || "—"}{i.recurring && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "var(--app-accent)", color: "#0a0a0a" }} data-testid={`recurring-badge-${i.id}`}>{t("RECURRING")} {i.recurrence_cadence}</span>}</td>
                  <td className="p-3 text-right">${i.amount.toFixed(2)}</td>
                  <td className="p-3">{i.due_date || "—"}</td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ background: i.paid ? "#9AE6B4" : "#FFD166", color: "#0a0a0a" }}>{i.paid ? t("Paid") : t("Unpaid")}</span>
                  </td>
                  <td className="p-3 text-right">
                    {!i.paid && <Button variant="ghost" size="sm" onClick={() => markPaid(i.id)} data-testid={`mark-paid-${i.id}`}><Check className="w-4 h-4 mr-1" /> {t("Mark paid")}</Button>}
                    <Button variant="ghost" size="icon" onClick={() => del(i.id)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
